/**
 * DRPX Courier — Auth-Hook
 *
 * Login, Register, Logout, Password Recovery. Verwaltet User-Session
 * via Supabase `onAuthStateChange`. Lädt globale Daten (Users, Aufträge,
 * Bewertungen, Rechnungen) nach Login.
 *
 * @module hooks/useAuth
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../lib/supabase.js';
import { checkRateLimit, RATE_LIMITS } from '../lib/rateLimit.js';
import { validateEmail, validatePassword } from '../lib/validation.js';
import { setMonitoringUser, trackEvent } from '../lib/monitoring.js';
import { initPlattformSettings } from '../config/constants.js';
import { startTab, istRecoveryLink, istEinladungLink } from '../config/routes.js';
import { safeGetLS } from '../lib/storage.js';
import { merkeSitzungAbgelaufen, merkeZugangInaktiv } from '../lib/sitzung.js';
import { istMitarbeiter, istBieter, personId, baueMitarbeiterSicht, rechteGleich } from '../lib/zugang.js';
import { authFehlerText } from '../lib/authFehler.js';
import type { Profile, Auftrag, Bewertung, TranslateFn } from '../types.js';
import type { Session } from '@supabase/supabase-js';

// ════════════════════════════════════════════════════════════════════
// Types
// ════════════════════════════════════════════════════════════════════

type ToastFn = (msg: string, type?: string) => void;

interface RegisterData {
  rolle: string;
  email: string;
  passwort: string;
  firma: string;
  inhaber: string;
  strasse: string;
  land: string;
  plz: string;
  ort: string;
  ustId?: string;
  steuernr: string;
  finanzamt: string;
  anrede: string;
  nachname: string;
  vorname: string;
  telefon: string;
  telefax?: string;
  notfallnr?: string;
  homepage?: string;
  avvAkzeptiertAm?: string | null;
}

interface AuthActionResult {
  error?: string;
  /**
   * Maschinenlesbarer Supabase-Error-Code (z.B. 'email_not_confirmed',
   * 'invalid_credentials', 'user_already_exists'). Wird vom Frontend
   * genutzt, um z.B. einen "Mail erneut senden"-Button bedingt
   * anzuzeigen. Kann `undefined` sein, wenn Supabase keinen Code
   * liefert (z.B. bei Netzwerkfehlern).
   */
  errorCode?: string;
  success?: boolean;
  mode?: string;
  /**
   * Partieller Erfolg: Hauptaktion geklappt, aber ein nicht-kritischer
   * Nebenschritt ist fehlgeschlagen. Anders als `error` blockiert das
   * den Erfolgs-Flow nicht — das Frontend zeigt eine Hinweis-Meldung
   * und lässt den User weitermachen.
   * Beispiel: Registrierung erfolgreich, aber KYC-Upload fehlgeschlagen
   *   → `warning: t('registerKycUploadFailed')` → User bekommt Toast
   *     "Registrierung erfolgreich, bitte KYC-Dokumente später im Profil
   *     erneut hochladen."
   */
  warning?: string;
}

interface UseAuthOptions {
  t: TranslateFn;
  nav: (target: string) => void;
  _setOnboardingStep?: (step: number) => void;
  /**
   * Dead prop (Pattern 14): wird aktuell nicht mehr benutzt, seit der
   * Stripe-Return-Handler nach `useAboLogic` (eigentlicher Owner) extrahiert
   * wurde. Die Prop bleibt in der Signatur, damit der Call-Site in App.tsx
   * nicht geändert werden muss. Bei der nächsten App.tsx-Änderung kann die
   * Übergabe weg.
   */
  openToast?: ToastFn;
}

interface UseAuthReturn {
  loading: boolean;
  currentUser: Profile | null;
  setCurrentUser: React.Dispatch<React.SetStateAction<Profile | null>>;
  users: Profile[];
  setUsers: React.Dispatch<React.SetStateAction<Profile[]>>;
  auftraege: Auftrag[];
  setAuftraege: React.Dispatch<React.SetStateAction<Auftrag[]>>;
  bewertungen: Bewertung[];
  setBewertungen: React.Dispatch<React.SetStateAction<Bewertung[]>>;
  /** Auftrags-IDs, die der Fahrer in "Meine Angebote → Nicht mehr verfügbar"
   *  weggeblendet hat (Migration 062). Leer für Firma/Admin. */
  dismissedAngeboteIds: string[];
  setDismissedAngeboteIds: React.Dispatch<React.SetStateAction<string[]>>;
  pendingZertsTotal: number;
  setPendingZertsTotal: React.Dispatch<React.SetStateAction<number>>;
  pendingDeletion: Profile | null;
  setPendingDeletion: React.Dispatch<React.SetStateAction<Profile | null>>;
  sbError: string | null;
  passwordRecovery: boolean;
  /** Nachweis aus dem Passwort-Link der Mail (`/recovery?token_hash=…`), noch
   *  NICHT eingelöst — das geschieht erst beim Speichern im Formular
   *  (17.10.2026). null = kein solcher Link. */
  recoveryToken: string | null;
  /** Nachweis aus dem Link der Team-Einladung (`/einladung?token_hash=…`,
   *  DRPX-Pro, Migration 141), noch NICHT eingelöst. null = kein solcher Link. */
  einladungToken: string | null;
  loadData: (rolle: string | undefined, userId?: string) => Promise<void>;
  login: (email: string, pw: string) => Promise<AuthActionResult>;
  register: (data: RegisterData) => Promise<AuthActionResult>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<AuthActionResult>;
  /**
   * Sendet die Signup-Bestätigungs-Mail erneut an die angegebene Email.
   * Wird vom Login-Screen genutzt, wenn der User sich einloggen möchte,
   * aber seine Mail noch nicht bestätigt hat.
   */
  resendConfirmation: (email: string) => Promise<AuthActionResult>;
}

// ════════════════════════════════════════════════════════════════════
// Hook
// ════════════════════════════════════════════════════════════════════

export function useAuth({ t, nav, _setOnboardingStep }: UseAuthOptions): UseAuthReturn {
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<Profile | null>(null);
  const [users, setUsers] = useState<Profile[]>([]);
  const [auftraege, setAuftraege] = useState<Auftrag[]>([]);
  const [bewertungen, setBewertungen] = useState<Bewertung[]>([]);
  const [dismissedAngeboteIds, setDismissedAngeboteIds] = useState<string[]>([]);
  const [pendingZertsTotal, setPendingZertsTotal] = useState(0);
  const [pendingDeletion, setPendingDeletion] = useState<Profile | null>(null);
  const [sbError, setSbError] = useState<string | null>(null);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  // Passwort-Link aus der Mail (/recovery?token_hash=…&type=recovery).
  // Seit 17.10.2026 wird er hier nur MITGENOMMEN, nicht eingelöst: Das
  // Formular „Neues Passwort" löst ihn erst beim Speichern ein. Vorher
  // geschah das schon beim Laden der Seite — Virenprüfer mancher Mail-Dienste
  // öffnen Links vorab in einem unsichtbaren Browser und verbrauchten ihn
  // damit („Link abgelaufen", obwohl gerade erst angefordert). Ein Tippfehler
  // im neuen Passwort verbrauchte ihn ebenfalls. Der Ausdruck läuft nur beim
  // ersten Rendern.
  const [recoveryToken] = useState<string | null>(function () {
    if (!istRecoveryLink()) return null;
    return new URLSearchParams(window.location.search).get('token_hash') || null;
  });
  // Link aus der Team-Einladung (/einladung?token_hash=…&type=invite,
  // DRPX-Pro, Migration 141). Wie der Passwort-Link nur mitgenommen; das
  // Formular löst ihn beim Speichern ein.
  const [einladungToken] = useState<string | null>(function () {
    if (!istEinladungLink()) return null;
    return new URLSearchParams(window.location.search).get('token_hash') || null;
  });
  // Solange das Einladungs-Formular offen ist, übergehen wir Anmelde-Signale:
  // Das Einlösen meldet SIGNED_IN, der Zugang ist dann aber noch
  // „eingeladen" — handleLogin würde ihn als inaktiv sofort wieder abmelden.
  // Nach dem Annehmen lädt das Formular die Seite neu.
  const einladungRef = useRef<boolean>(!!einladungToken);
  const loggedInRef = useRef(false);
  // true, sobald der Nutzer selbst auf „Abmelden" geklickt hat — dann ist
  // das folgende SIGNED_OUT gewollt und kein „Sitzung abgelaufen".
  const selbstAbgemeldetRef = useRef(false);
  // DRPX-Pro (Weg B, 16.10.2026): Firmen-Sicht eines angemeldeten
  // Mitarbeiters (id = Firma, personId = Person). `loadData` braucht sie,
  // weil Aufrufer nur Rolle und ID mitgeben und die Liste aus
  // `profiles_public` dem Mitarbeiter das Firmenprofil nicht zeigt.
  const firmenSichtRef = useRef<Profile | null>(null);

  // ═══ Load data from Supabase ═══
  const loadData = useCallback(async function (rolle: string | undefined, userId?: string) {
    try {
      // Profile zuerst laden — daraus leiten wir ab, ob der aktuelle User
      // Marktplatz-Aufträge sehen soll: Fahrer ODER freigeschaltete
      // Spedition (Firma mit darfBieten, Migration 105/106) — auch ihr
      // Mitarbeiter. Das Flag steckt nicht im JWT, daher der Umweg über das
      // geladene Profil.
      const profilesFn = rolle === 'admin' ? db.getAllProfilesFull() : db.getAllProfiles();
      const usersRes = await profilesFn;
      // Mitarbeiter (DRPX-Pro): Die Firmen-Sicht kommt mit in die Liste, damit
      // Nachschlagen der eigenen Firma (`users.find(u => u.id === user.id)`)
      // wie beim Chef funktioniert — `profiles_public` zeigt ihm nur sich selbst.
      const sicht = firmenSichtRef.current;
      let liste = usersRes.data || [];
      if (sicht) liste = [sicht].concat(liste.filter((u) => u.id !== sicht.id));
      if (usersRes.data) setUsers(liste);

      const me = sicht || (userId ? liste.find((u) => u.id === userId) : undefined);
      const bieter = istBieter({ rolle, darfBieten: me?.darfBieten });

      const [auftraegeRes, bewertungenRes, settingsRes, zertsRes] = await Promise.all([
        db.getAuftraege(bieter),
        db.getBewertungen(),
        db.getPlattformSettings(),
        db.getAllPendingZerts(),
      ]);
      if (auftraegeRes.data) setAuftraege(auftraegeRes.data);
      if (bewertungenRes.data) setBewertungen(bewertungenRes.data);
      // Nur bei Erfolg anwenden: bei einem Fehler kommt `{}` zurück — das darf
      // die gespeicherten Werte nicht überschreiben (18.10.2026).
      if (!settingsRes.error && settingsRes.data) initPlattformSettings(settingsRes.data as Record<string, unknown>);
      setPendingZertsTotal((zertsRes.data || []).length);

      // Bieter-spezifisch: weggeblendete Angebote (Migration 062)
      // WICHTIG: Bei userId-less Aufrufen (Realtime-Reload aus useNotifications)
      // die Dismissed-Liste NICHT anfassen, sonst tauchen weggeblendete
      // Karteileichen bei jeder Notification wieder auf. Die Liste wird
      // nur beim Login geladen und beim Logout gecleart.
      //
      // 16.10.2026: vorher nur für Rolle `fahrer` — eine Spedition (Firma mit
      // darfBieten) blendete aus, nach dem Neuladen war alles wieder da. Die
      // Liste ist persönlich (Entscheidung 16.10.2026): beim Mitarbeiter die
      // eigene Konto-ID, nicht die der Firma.
      if (bieter && userId) {
        const dismissedRes = await db.getFahrerAngebotDismissed(sicht ? personId(sicht) : userId);
        if (!dismissedRes.error) setDismissedAngeboteIds(dismissedRes.data);
      } else if (rolle && !bieter) {
        // Kein Bieter (mehr): clearen
        setDismissedAngeboteIds([]);
      }
      // Bieter ohne userId → State unverändert lassen
    } catch (e: unknown) {
      console.error('Data load error:', e);
    }
  }, []);

  // ═══ Abmelden (gewollt) und neu laden ═══
  // Von `logout` und vom Login eines nicht aktiven Mitarbeiters genutzt.
  const abmeldenUndNeuLaden = useCallback(async function () {
    // 0. Merken, dass das folgende SIGNED_OUT gewollt ist (kein Hinweis
    //    „Sitzung abgelaufen").
    selbstAbgemeldetRef.current = true;
    // 1. Erst signOut aufrufen — verwendet noch das vorhandene Token,
    //    um den Refresh-Token server-seitig zu revokieren. Wichtig:
    //    Wenn wir vorher localStorage leeren, findet der Client den Token
    //    nicht mehr → Revoke-Request schlägt fehl → Token bleibt gültig,
    //    kann gestohlen werden. `await` (kein fire-and-forget) ist
    //    essenziell, sonst wird der Request durch `window.location.reload()`
    //    abgebrochen.
    try {
      await db.signOut();
    } catch (e: unknown) {
      // Wir loggen trotzdem aus — selbst wenn der Server nicht erreichbar
      // ist, der User will einfach nur raus aus der App.
      console.warn('signOut failed, cleaning up anyway:', e);
    }
    // 2. Jetzt localStorage leeren (Session-Artefakte aus Browser entfernen).
    try {
      Object.keys(localStorage)
        .filter(function (k) { return k.startsWith('sb-'); })
        .forEach(function (k) { localStorage.removeItem(k); });
    } catch { /* ignored */ }
    // 3. Reload um sicherzustellen dass In-Memory-State weg ist.
    window.location.reload();
  }, []);

  // ═══ Gemeinsame Login-Logik (DRY) ═══
  const handleLogin = useCallback(async function (session: Session) {
    if (loggedInRef.current) return;
    // Erst das Profil, dann die Daten (16.10.2026, DRPX-Pro). Vorher liefen
    // beide gleichzeitig, und `loadData` bekam die Rolle aus den Anmelde-
    // Angaben (user_metadata). Die stammen aus der Registrierung und ändern
    // sich nie — bei einem Mitarbeiter steht dort z. B. noch „firma". Jetzt
    // zählt die Rolle aus dem Profil.
    const res = await db.getProfile(session.user.id);
    if (res.error) {
      const errMsg = typeof res.error === 'object' && res.error !== null && 'message' in res.error
        ? String((res.error as { message: unknown }).message)
        : String(res.error);
      setSbError(t('hcProfilFehler') + errMsg);
      return;
    }
    if (res.data) {
      let profile: Profile = res.data;
      if (profile.geloeschtAm) {
        loggedInRef.current = true;
        setPendingDeletion(profile);
        setMonitoringUser(profile.id);
        return;
      }
      // Mitarbeiter (DRPX-Pro, Weg B): In der App arbeitet er als seine
      // Firma — Firmendaten über `get_meine_firma()` (Migration 139).
      // Kommt nichts zurück, ist der Zugang nicht aktiv (eingeladen,
      // gesperrt, Firma gelöscht): abmelden, Hinweis auf der Anmeldeseite.
      if (istMitarbeiter(profile)) {
        const firmaRes = await db.getMeineFirma();
        if (firmaRes.error) {
          const errMsg = typeof firmaRes.error === 'object' && firmaRes.error !== null && 'message' in firmaRes.error
            ? String((firmaRes.error as { message: unknown }).message)
            : String(firmaRes.error);
          setSbError(t('hcProfilFehler') + errMsg);
          return;
        }
        if (!firmaRes.data) {
          merkeZugangInaktiv();
          await abmeldenUndNeuLaden();
          return;
        }
        // Rechte vom Chef (Migration 141). Fehlen sie, zeigt die App
        // vorsichtshalber nichts davon (lib/zugang.ts → darf).
        const rechte = await db.getMeineTeamRechte(profile.id);
        profile = baueMitarbeiterSicht(profile, firmaRes.data, rechte);
        firmenSichtRef.current = profile;
      }
      await loadData(profile.rolle, profile.id);
      loggedInRef.current = true;
      setCurrentUser(profile);
      setMonitoringUser(personId(profile));
      // Nur nach einem ECHTEN Login auf die Startseite der Rolle springen.
      //
      // Fehler gefunden am 16.08.2026: handleLogin läuft an zwei Stellen —
      // bei SIGNED_IN (echter Login) und bei INITIAL_SESSION (jeder
      // Seitenaufruf mit bestehender Sitzung). Im zweiten Fall warf der
      // Sprung die aufgerufene Adresse weg: Wer /admin/users eintippte,
      // landete auf /admin, ein Fahrer auf dem Marktplatz. Damit hat noch
      // nie ein direkter Link funktioniert — weder Lesezeichen noch die
      // Links aus den Benachrichtigungs-Mails.
      //
      // Der Sprung ist nur sinnvoll, wenn es kein eigenes Ziel gibt: auf
      // der Wurzel oder direkt nach dem Login-Formular. Jede andere
      // Adresse ist das erklärte Ziel des Nutzers und bleibt bestehen.
      const zielPfad = window.location.pathname;
      if (zielPfad === '/' || zielPfad === '/login') {
        nav(startTab(profile.rolle));
      }
      db.updateProfile(personId(profile), { lastActive: new Date().toISOString() }).catch(function () {});
    }
  }, [t, nav, loadData, abmeldenUndNeuLaden]);

  // ═══ Passwort-Link: Nachweis aus der Adresse entfernen ═══
  // Er steht danach nur noch im Speicher (recoveryToken) und landet nicht im
  // Verlauf. Eingelöst wird er im Formular beim Speichern
  // (`PasswordRecoveryForm`, 17.10.2026).
  // Gleiches für den Einladungs-Link (17.10.2026).
  useEffect(function () {
    if (!recoveryToken && !einladungToken) return;
    window.history.replaceState(window.history.state, '', window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ═══ Supabase Auth — onAuthStateChange ═══
  useEffect(function () {
    let timedOut = false;
    const timeout = setTimeout(function () {
      timedOut = true;
      setLoading(false);
      setSbError(t('hcSupabaseTimeout'));
    }, 8000);

    const sub = db.onAuthStateChange(async function (event, session) {
      if (event === 'INITIAL_SESSION') {
        clearTimeout(timeout);
        // Einladungs-Formular offen: keine bestehende Sitzung übernehmen
        // (siehe einladungRef).
        if (session && !einladungRef.current) {
          try {
            await handleLogin(session);
          } catch (e: unknown) {
            // Zombie-State verhindern: wenn handleLogin unterwegs
            // `loggedInRef = true` gesetzt hat und dann crashed, würde
            // ein Retry nicht mehr funktionieren. Hier defensiv resetten.
            loggedInRef.current = false;
            const msg = e instanceof Error ? e.message : String(e);
            setSbError(t('hcSupabaseFehler') + ' ' + msg);
          }
        }
        // setLoading(false) nur aufrufen wenn der Timeout nicht schon
        // gefeuert hat (sonst blinkender State-Wechsel).
        if (!timedOut) setLoading(false);
        return;
      }
      if (event === 'SIGNED_IN' && session) {
        if (einladungRef.current) return;
        try {
          await handleLogin(session);
        } catch (e: unknown) {
          loggedInRef.current = false;
          console.warn('Auth SIGNED_IN error:', e);
        }
        return;
      }
      if (event === 'SIGNED_OUT') {
        const warAngemeldet = loggedInRef.current;
        loggedInRef.current = false;
        // Nicht selbst abgemeldet, aber vorher angemeldet → die Sitzung ist
        // abgelaufen (Refresh Token ungültig) oder wurde in einem anderen
        // Tab beendet. Bis 08.10.2026 blieb die App dann im Geisterzustand:
        // Nutzer sichtbar, jede Aktion scheiterte an der RLS („42501").
        // Jetzt wie beim Abmelden neu laden; die Anmeldeseite zeigt den
        // Hinweis (Merker in lib/sitzung.ts). Supabase hat seine eigenen
        // Schlüssel in diesem Fall schon selbst entfernt.
        if (warAngemeldet && !selbstAbgemeldetRef.current) {
          merkeSitzungAbgelaufen();
          window.location.reload();
        }
        return;
      }
      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true);
      }
    });

    return function () {
      clearTimeout(timeout);
      if (sub) sub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ═══ Mitarbeiter: Zugang und Rechte laufend prüfen (17.10.2026) ═══
  // Sperrt oder entfernt der Chef einen Mitarbeiter (oder entzieht der Admin
  // DRPX-Pro), sperrt die Datenbank sofort — die offene Seite merkte das aber
  // erst beim Neuladen (Live-Test Sertan). Jetzt: jede Minute und beim
  // Zurückkehren in den Tab nachsehen. Kein aktiver Zugang mehr → abmelden,
  // Anmeldeseite zeigt „Dein Zugang ist nicht aktiv". Geänderte Rechte gelten
  // sofort (Knöpfe, Menü, Abrechnung). Netzfehler → nichts tun, nächster Versuch.
  const mitarbeiterId = currentUser && istMitarbeiter(currentUser) ? personId(currentUser) : '';
  useEffect(function () {
    if (!mitarbeiterId) return;
    let laeuft = false;
    let beendet = false;
    async function pruefen() {
      if (laeuft || beendet) return;
      laeuft = true;
      try {
        const firmaRes = await db.getMeineFirma();
        if (beendet || firmaRes.error) return;
        if (!firmaRes.data) {
          beendet = true;
          merkeZugangInaktiv();
          await abmeldenUndNeuLaden();
          return;
        }
        const rechte = await db.getMeineTeamRechte(mitarbeiterId);
        if (beendet || !rechte) return;
        const sicht = firmenSichtRef.current;
        if (sicht && !rechteGleich(sicht.teamRechte, rechte)) {
          firmenSichtRef.current = { ...sicht, teamRechte: rechte };
          setCurrentUser(function (prev) {
            return prev ? { ...prev, teamRechte: rechte } : prev;
          });
        }
      } catch (e: unknown) {
        console.warn('Zugangsprüfung:', e);
      } finally {
        laeuft = false;
      }
    }
    function beiSichtbar() {
      if (document.visibilityState === 'visible') void pruefen();
    }
    const iv = setInterval(function () { void pruefen(); }, 60 * 1000);
    document.addEventListener('visibilitychange', beiSichtbar);
    window.addEventListener('focus', beiSichtbar);
    return function () {
      beendet = true;
      clearInterval(iv);
      document.removeEventListener('visibilitychange', beiSichtbar);
      window.removeEventListener('focus', beiSichtbar);
    };
  }, [mitarbeiterId, abmeldenUndNeuLaden]);

  // ═══ Auth-Error-Code → i18n-Text ═══
  // Seit 17.10.2026 in `lib/authFehler.ts` (auch für „Passwort vergessen").
  const mapAuthError = useCallback(
    function (code: string | undefined, fallback: string): string {
      return authFehlerText(code, fallback, t);
    },
    [t],
  );

  // ═══ Login ═══
  const login = useCallback(
    async function (email: string, pw: string): Promise<AuthActionResult> {
      try {
        const cleanEmail = email.toLowerCase().trim();
        // Format-Check: verhindert Müll-Keys im Rate-Limiter + generische
        // Error-Message (kein Leak ob die Email existiert).
        if (validateEmail(cleanEmail) !== 'ok') {
          return { error: t('authErrInvalidCredentials') };
        }
        if (!pw || pw.length < 1 || pw.length > 128) {
          return { error: t('authErrInvalidCredentials') };
        }
        const rl = await checkRateLimit(RATE_LIMITS.LOGIN, cleanEmail);
        if (!rl.ok) return { error: t('hcRateLimitExceeded') };
        const res = await db.signIn(cleanEmail, pw);
        if (res.error) {
          return {
            error: mapAuthError(res.error.code, res.error.message),
            errorCode: res.error.code,
          };
        }
        if (!res.data.user) return { error: t('hcVerbindungsfehler') };
        try { trackEvent('login'); } catch { /* ignored */ }
        return {};
      } catch (e: unknown) {
        console.error('Login fetch error:', e);
        return { error: t('hcVerbindungsfehler') };
      }
    },
    [t, mapAuthError],
  );

  // ═══ Bestätigungs-Mail erneut senden ═══
  // Wird aufgerufen, wenn der User beim Login `email_not_confirmed`
  // bekommt. Frontend sollte nach erfolgreichem Call einen Cooldown-
  // Timer anzeigen (sonst kann der User in einer Schleife klicken).
  const resendConfirmation = useCallback(
    async function (email: string): Promise<AuthActionResult> {
      try {
        const cleanEmail = email.toLowerCase().trim();
        if (!cleanEmail) return { error: t('loginErrEmpty') };
        // Format-Check zusätzlich zum Leer-Check — verhindert Müll-Keys
        // im Rate-Limiter und schützt vor Tipp-Fehlern die zu Spam-
        // Mails an falsche Adressen führen.
        if (validateEmail(cleanEmail) !== 'ok') {
          return { error: t('errEmail') };
        }
        // Zusätzliches Rate-Limit (Supabase selbst hat auch eins, aber
        // client-seitig schützen wir vor Spam-Klicks via gleichem Rate-
        // Limit wie Signup).
        const rl = await checkRateLimit(RATE_LIMITS.SIGNUP, cleanEmail);
        if (!rl.ok) return { error: t('hcRateLimitExceeded') };
        const res = await db.resendConfirmation(cleanEmail);
        if (res.error) {
          return {
            error: mapAuthError(res.error.code, res.error.message),
            errorCode: res.error.code,
          };
        }
        return {};
      } catch (e: unknown) {
        console.error('Resend fetch error:', e);
        return { error: t('hcVerbindungsfehler') };
      }
    },
    [t, mapAuthError],
  );

  // ═══ Register ═══
  const register = useCallback(
    async function (data: RegisterData): Promise<AuthActionResult> {
      try {
        // Defense-in-Depth: Email + Passwort nochmal validieren, auch wenn
        // useRegisterForm das bereits tut. Falls register() mal von
        // anderer Stelle aufgerufen wird, greift der Check trotzdem.
        const cleanEmail = data.email.toLowerCase().trim();
        if (validateEmail(cleanEmail) !== 'ok') {
          return { error: t('errEmail') };
        }
        if (!validatePassword(data.passwort)) {
          return { error: t('errPwMin') };
        }

        const rl = await checkRateLimit(RATE_LIMITS.SIGNUP, cleanEmail);
        if (!rl.ok) return { error: t('hcRateLimitExceeded') };

        // Finding E3 (18.04.2026): Explizite AGB/DSGVO-Zustimmung als
        // Signup-Metadaten mitsenden. Der handle_new_user-Trigger prüft
        // diese Felder und wirft Exception, wenn sie fehlen. So wird das
        // implizite `now()`-Setting durch eine nachweisbare User-Action
        // ersetzt.
        //
        // User-Agent wird als Audit-Trail gespeichert (navigator.userAgent
        // ist im Browser nicht-sensitiv, kommt eh in jedem HTTP-Request).
        // IP wird bewusst NICHT erfasst — DSGVO Art. 7 verlangt nur
        // Nachweisbarkeit, Timestamp+Version+UA reichen.
        const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
        const uaTrimmed = ua.length > 1000 ? ua.slice(0, 1000) : ua;
        const version = 'v2026.03';  // Aktuelle AGB/DSGVO-Fassung

        // Aktuelle Sprache für Auth-Mail-Templates (Supabase Template-Language-Switch).
        // Liest denselben localStorage-Key wie der I18nProvider — damit auch dann funktional,
        // wenn der User die Sprache im Signup-Screen gewählt hat.
        // Werte werden direkt im Template verglichen (`{{ if eq .Data.language "en" }}`),
        // nicht-'en' fällt auf den Deutsch-Zweig zurück.
        const currentLang = safeGetLS('drpx_lang') || 'de';

        const res = await db.signUp(cleanEmail, data.passwort, {
          rolle: data.rolle,
          firma: data.firma,
          language: currentLang,
          // AGB/DSGVO-Zustimmung — wird vom DB-Trigger validiert
          agb_akzeptiert: version,
          dsgvo_akzeptiert: version,
          // AVV-Zustimmung (DSGVO Art. 28) — der handle_new_user-Trigger
          // (Migration 085) verlangt dieses Feld zwingend, sonst schlägt der
          // Sign-up mit "Database error saving new user" fehl. Das Frontend
          // erfasst die Zustimmung als Pflicht-Checkbox (useRegisterForm avvOk).
          avv_akzeptiert: version,
          agb_akzeptiert_ua: uaTrimmed,
          dsgvo_akzeptiert_ua: uaTrimmed,
          // Variante C / Sitzung Y (20.05.2026):
          // Stammdaten werden als Signup-Metadaten mitgesendet. Der
          // handle_new_user-Trigger (Migration 046/084) liest sie aus
          // raw_user_meta_data und schreibt sie direkt ins Profil.
          // Vorher: Frontend versuchte nach signUp() ein UPDATE auf
          // profiles zu fahren — schlug aber an RLS fehl, weil bei
          // aktivierter Mail-Confirmation nach signUp() keine Session
          // existiert (auth.uid() = NULL). Stammdaten gingen verloren,
          // KYC-Upload ebenfalls. Mit dem neuen Pfad geht alles in
          // einem Wurf über den SECURITY-DEFINER-Trigger.
          inhaber: data.inhaber || '',
          strasse: data.strasse || '',
          land: data.land || 'DE',
          plz: data.plz || '',
          ort: data.ort || '',
          ustId: data.ustId || '',
          steuernr: data.steuernr || '',
          finanzamt: data.finanzamt || '',
          anrede: data.anrede || '',
          nachname: data.nachname || '',
          vorname: data.vorname || '',
          telefon: data.telefon || '',
        });
        if (res.error) {
          return {
            error: mapAuthError(res.error.code, res.error.message),
            errorCode: res.error.code,
          };
        }
        // Variante C: Kein UPDATE / KYC-Upload mehr nach signUp().
        // Alle Stammdaten kamen in den Metadaten mit und sind durch den
        // Trigger schon im Profil gelandet. KYC-Upload macht der User
        // nach Mail-Bestätigung + Login im Profil-Bereich.
        return {};
      } catch (e: unknown) {
        console.error('Register fetch error:', e);
        return { error: t('hcVerbindungsfehler') };
      }
    },
    [t, mapAuthError],
  );

  // ═══ Logout ═══
  const logout = abmeldenUndNeuLaden;

  // ═══ Password Reset ═══
  const resetPassword = useCallback(async function (email: string): Promise<AuthActionResult> {
    const cleanEmail = email.toLowerCase().trim();
    // Format-Check: verhindert Müll-Keys im Rate-Limiter.
    if (validateEmail(cleanEmail) !== 'ok') {
      return { error: t('errEmail') };
    }
    const rl = await checkRateLimit(RATE_LIMITS.PW_RESET, cleanEmail);
    if (!rl.ok) return { error: t('hcRateLimitExceeded') };
    const res = await db.resetPasswordForEmail(cleanEmail);
    if (res.error) {
      return {
        error: mapAuthError(res.error.code, res.error.message),
        errorCode: res.error.code,
      };
    }
    return { success: true, mode: 'supabase' };
  }, [t, mapAuthError]);

  return {
    loading, currentUser, setCurrentUser,
    users, setUsers,
    auftraege, setAuftraege,
    bewertungen, setBewertungen,
    dismissedAngeboteIds, setDismissedAngeboteIds,
    pendingZertsTotal, setPendingZertsTotal,
    pendingDeletion, setPendingDeletion,
    sbError, passwordRecovery, recoveryToken, einladungToken,
    loadData, login, register, logout, resetPassword, resendConfirmation,
  };
}
